import { useCallback, useEffect, useState, useRef } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { isSafari } from 'react-device-detect';

interface NotificationOptions {
  onlyIfNotVisible?: boolean;
}

const ALERT_ICON = '(1) ';

const useNotificationService = (options?: NotificationOptions) => {
  const isVisible = usePageVisibility();
  const alarm = require('../../../assets/notificationSound.mp3').default;
  const [clearFunction, setClearFunction] = useState<(() => void) | undefined>(
    undefined,
  );
  const onlyIfNotVisible =
    !options || options.onlyIfNotVisible === undefined
      ? true
      : options.onlyIfNotVisible;

  useEffect(() => {
    if (isVisible && clearFunction) {
      clearFunction();
      setClearFunction(undefined);
    }

    return clearFunction;
  }, [isVisible, clearFunction]);

  const createLinkElement = () => {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = '../../../../public/favicon.ico';
    document.getElementsByTagName('head')[0].appendChild(link);
    return link;
  };

  const canUseNotification = () =>
    'Notification' in window && window.Notification;

  const notificationPermissionGranted = useCallback(
    () => canUseNotification() && window.Notification.permission === 'granted',
    [],
  );

  const requestNotification = useCallback(() => {
    if (canUseNotification() && !notificationPermissionGranted()) {
      window.Notification.requestPermission();
    }
  }, [notificationPermissionGranted]);

  let timer = useRef<NodeJS.Timeout | undefined>(undefined);
  // Debounce needs timer with current value, userRef is needed
  const debounce = (mainFunction: any, delay: number) => {
    return function (...args: any[]) {
      clearTimeout(timer.current);

      timer.current = setTimeout(() => {
        mainFunction(...args);
      }, delay);
    };
  };

  const playNotificationSound = useCallback(() => {
    const audio = new Audio(alarm);
    audio.loop = false;

    const debounceAlert = debounce(() => {
      audio.play()?.catch(() => console.log('Sound was blocked')); // throws error when page loaded in background
    }, 1500);

    debounceAlert();
  }, [alarm]);

  const sendSystemNotification = useCallback(
    (
      notificationTitle: string,
      notificationText: string,
      rerequestNotification: boolean,
      autoCloseAfter?: number,
    ) => {
      if (notificationPermissionGranted()) {
        const notification = new window.Notification(notificationTitle, {
          body: notificationText,
        });
        notification.onclick = () => window.focus();
        if (autoCloseAfter !== undefined) {
          setTimeout(() => {
            notification.close();
          }, autoCloseAfter);
        }
      } else if (rerequestNotification) {
        requestNotification();
      }
    },
    [notificationPermissionGranted, requestNotification],
  );

  const changeFavicon = useCallback(
    (afterTimeout: number, loopInterval: number, icons: string[]) => {
      if (clearFunction) {
        return clearFunction;
      }
      const link =
        document.querySelector<HTMLLinkElement>("link[rel~='icon']") ??
        createLinkElement();
      const href = link.href;
      const title = document.title;
      let interval: NodeJS.Timeout;
      // if called multiple times
      if (icons.length > 0) {
        link.href = icons.length > 1 ? icons[1] : icons[0];
        if (isSafari) {
          document.title =
            document.title === title ? `${ALERT_ICON}${title}` : title;
        }

        const timeout = setTimeout(() => {
          if (!isSafari) {
            interval = setInterval(() => {
              link.href =
                icons[
                  (icons.findIndex((icon) => icon === link.href) + 1) %
                    icons.length
                ];
            }, loopInterval);
          }
        }, afterTimeout);

        return () => {
          link.href = href;
          document.title = title;
          clearTimeout(timeout);
          if (interval) {
            clearInterval(interval);
          }
        };
      }
      return undefined;
    },
    [clearFunction],
  );

  const notify = useCallback(
    (
      notificationTitle: string,
      notificationText: string,
      playSound: boolean = false,
      rerequestNotification: boolean = false,
      autoCloseAfter?: number,
    ) => {
      if (!onlyIfNotVisible || !isVisible) {
        sendSystemNotification(
          notificationTitle,
          notificationText,
          rerequestNotification,
          autoCloseAfter,
        );

        if (playSound) {
          playNotificationSound();
        }

        const clearTitle = changeFavicon(30000, 1000, [
          `${window.location.origin}/favicon.ico`,
          `${window.location.origin}/favicon-notification.ico`,
        ]);
        setClearFunction(() => clearTitle); // we need save function (not call it)
      }
    },
    [
      changeFavicon,
      isVisible,
      onlyIfNotVisible,
      playNotificationSound,
      sendSystemNotification,
    ],
  );

  return {
    notify,
    requestNotification: () => {
      requestNotification();
    },
  };
};

export default useNotificationService;
